import { ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import { NavLink } from 'react-router-dom'

import { cn } from '@/lib/misc/Utils'
import { FLAGS, useFeatureFlag } from '@/lib/hooks/useFeatureFlag'

import BellIcon from '@icons/bell'
import BriefcaseIcon from '@icons/briefcase'
import ShieldCheckIcon from '@icons/shield-check'
import UsersIcon from '@icons/users'

interface ItemInterface {
  id: string
  label: string
  link: string
  icon: ReactElement
}

const SettingsNav = ({ activeItem }: { activeItem: string }): ReactElement => {
  const isFlexInsightsEnabled = useFeatureFlag(FLAGS.FLEX_INSIGHTS)
  const isTwoFactorEnforcementEnabled = useFeatureFlag(
    FLAGS.TWO_FACTOR_ENFORCEMENT
  )

  const items: ItemInterface[] = [
    {
      id: 'users',
      label: 'Users',
      link: '/settings/users',
      icon: <UsersIcon />,
    },
    {
      id: 'company-profile',
      label: 'Company Profile',
      link: '/settings/company-profile',
      icon: <BriefcaseIcon />,
    },
  ]

  if (isTwoFactorEnforcementEnabled) {
    items.push({
      id: 'security',
      label: 'Security',
      link: '/settings/security',
      icon: <ShieldCheckIcon />,
    })
  }

  if (isFlexInsightsEnabled) {
    items.push({
      id: 'flex_insights',
      label: 'FlexInsights',
      link: '/settings/flex-insights',
      icon: <BellIcon />,
    })
  }

  const navRef = useRef<HTMLElement>(null)
  const [hasMoreToRight, setHasMoreToRight] = useState(false)

  const updateOverflow = useCallback(() => {
    const nav = navRef.current
    if (!nav) return
    setHasMoreToRight(nav.scrollLeft + nav.clientWidth < nav.scrollWidth - 4)
  }, [])

  useEffect(() => {
    const nav = navRef.current
    if (!nav) return
    const active = nav.querySelector<HTMLElement>('[aria-current="page"]')
    if (active) {
      nav.scrollTo({
        left: active.offsetLeft - (nav.clientWidth - active.offsetWidth) / 2,
      })
    }
    updateOverflow()
    window.addEventListener('resize', updateOverflow)
    return () => window.removeEventListener('resize', updateOverflow)
  }, [activeItem, items.length, updateOverflow])

  return (
    <nav
      ref={navRef}
      aria-label="Settings"
      onScroll={updateOverflow}
      className={cn(
        'flex shrink-0 items-center gap-1 overflow-x-auto border-b border-hairline scrollbar-hidden',
        hasMoreToRight &&
          '[mask-image:linear-gradient(to_right,black_calc(100%-32px),transparent)]'
      )}
    >
      {items.map((item) => {
        const isActive = item.id === activeItem
        return (
          <NavLink
            key={item.id}
            to={item.link}
            aria-current={isActive ? 'page' : undefined}
            className={cn(
              'group -mb-px flex shrink-0 items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors',
              isActive
                ? 'border-accent-60 text-accent-60'
                : 'border-transparent text-neutral-50 hover:text-neutral-100'
            )}
          >
            <span className="size-4 shrink-0">{item.icon}</span>
            <span>{item.label}</span>
          </NavLink>
        )
      })}
    </nav>
  )
}

export default SettingsNav
